import React from 'react';
import { FaPlus as FaPlusSolid } from 'react-icons/fa';

import ShortcutHint from 'components/common/ShortcutHint/ShortcutHint';
import { TASKS_GRID_QUICK_ADD_ARIA_LABEL } from 'constants/uiConstants';
import { useElementWidth } from 'hooks/common/useElementWidth';
import { getInputTaskTypeLabel, InputTaskType } from 'interfaces/IInputTask';
import { pxToRem } from 'utils/cssUnits';

import { TaskKind } from './taskFactory';

// Display order, and the Ctrl/⌘ number that adds each type (OP-2534).
// Alt / ⌥ + a letter (OP-2556), not Ctrl / ⌘ + a number: those are the
// browser's own "switch to tab" shortcuts. Matched on the physical key
// (`shortcutCode`), since ⌥ + a letter types a character on a Mac.
export const QUICK_ADD_TASK_OPTIONS: {
  kind: TaskKind;
  taskType: InputTaskType;
  shortcutKey: string;
  shortcutCode: string;
}[] = [
  {
    kind: 'application',
    taskType: InputTaskType.Application,
    shortcutKey: 'A',
    shortcutCode: 'KeyA',
  },
  {
    kind: 'cropEstablishment',
    taskType: InputTaskType.CropEstablishment,
    shortcutKey: 'C',
    shortcutCode: 'KeyC',
  },
  {
    kind: 'production',
    taskType: InputTaskType.Production,
    shortcutKey: 'P',
    shortcutCode: 'KeyP',
  },
  {
    kind: 'otherCost',
    taskType: InputTaskType.Generic,
    shortcutKey: 'O',
    shortcutCode: 'KeyO',
  },
];

interface QuickAddTaskRowProps {
  onAdd: (kind: TaskKind) => void;
  /** At the row's right end: the new layout's default water rate (OP-2556). */
  trailing?: React.ReactNode;
}

// Sits at the top of the tasks grid, directly above the column headers
// (OP-2534): one button per task type, each with its Ctrl/⌘ shortcut, so a
// task can be added by mouse or keyboard without opening a menu. The new task
// is pinned directly beneath it.
// Below this width (rem), in the new layout, the buttons drop their
// shortcut hints so the row stays on one line (OP-2556). Widened for the
// fourth button, Other cost (OP-2399).
const COMPACT_BELOW_REM = 60;

const QuickAddTaskRow: React.FC<QuickAddTaskRowProps> = ({
  onAdd,
  trailing,
}) => {
  const { ref, width } = useElementWidth<HTMLDivElement>();
  const isCompact =
    !!trailing && width > 0 && pxToRem(width) < COMPACT_BELOW_REM;

  return (
    <div
      ref={ref}
      role="group"
      aria-label={TASKS_GRID_QUICK_ADD_ARIA_LABEL}
      className="flex flex-wrap items-center gap-2 bg-white px-3 py-2"
    >
      {QUICK_ADD_TASK_OPTIONS.map(({ kind, taskType, shortcutKey }) => (
        <button
          key={kind}
          type="button"
          className="inline-flex items-center gap-2 rounded-[var(--radius-button)] border border-solid border-gray-argent bg-white px-2 py-1 text-sm font-semibold text-secondary-dark hover:bg-gray-bright"
          onClick={() => onAdd(kind)}
        >
          {/* The app's add pattern: a green filled circle behind the +, so the
            buttons stand out without three solid fills (OP-2556). */}
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-success">
            <FaPlusSolid className="text-white" size={8} aria-hidden />
          </span>
          {getInputTaskTypeLabel(taskType)}
          {!isCompact && (
            <span className="text-gray-muted">
              <ShortcutHint
                shortcutKey={shortcutKey}
                variant="subtle"
                modifier="altOption"
              />
            </span>
          )}
        </button>
      ))}
      {trailing && <div className="ml-auto">{trailing}</div>}
    </div>
  );
};

export default QuickAddTaskRow;
